import React from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { HardDrive, Calendar, Clock, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useProfileStorage } from "@/hooks/useProfileStorage";
import { useChallengeProgress } from "@/hooks/useChallengeProgress";
import { ChallengeCard } from "@/components/challenges/ChallengeCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import DashboardProfileHero from "@/components/dashboard/DashboardProfileHero";
import { CreateSection } from "@/components/dashboard/CreateSection";
import { EmailConfirmationBanner } from "@/components/auth/EmailConfirmationBanner";
import { FEATURE_CHALLENGES } from "@/lib/constants";

const Dashboard = () => {
  const { user } = useAuth();
  const { data: dashboardData, isLoading: dashboardLoading } = useDashboardData();
  const { storageUsage, getStoragePercentage, isStorageWarning, isStorageCritical, isLoading: storageLoading } = useProfileStorage();
  const { challengeStatuses, isLoading: challengesLoading, triggerProgressUpdate } = useChallengeProgress();
  const { t, i18n } = useTranslation('dashboard');
  
  // Extract first name for personalized greeting
  const firstName = user?.user_metadata?.firstName || user?.email?.split('@')[0] || 'Freund';

  // Only show loading if both dashboard and storage are loading
  const isMainLoading = dashboardLoading;

  // Trigger progress update when dashboard data changes (but only once per session)
  React.useEffect(() => {
    if (!FEATURE_CHALLENGES) return;
    if (dashboardData && challengeStatuses.length > 0 && !challengesLoading) {
      // Only trigger update if we have actual data and challenges are loaded
      const hasUpdated = sessionStorage.getItem('challenge-progress-updated');
      if (!hasUpdated) {
        triggerProgressUpdate();
        sessionStorage.setItem('challenge-progress-updated', 'true');
      }
    }
  }, [dashboardData, challengeStatuses.length, challengesLoading, triggerProgressUpdate]);

  if (isMainLoading) {
    return (
      <AppShell 
        showHeader={false} 
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto p-4">
          {/* Hero Section Loading */}
          <div className="w-full py-8 px-4 mb-6">
            <div className="container mx-auto flex flex-col items-center space-y-6">
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="text-center space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
          </div>
          
          {/* Loading skeletons for KPI Cards */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex">
                <Card className="flex-1">
                  <CardHeader className="p-4 pb-2">
                    <Skeleton className="h-4 w-20" />
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <Skeleton className="h-8 w-12 mb-1" />
                    <Skeleton className="h-3 w-16" />
                    {i === 2 && <Skeleton className="h-2 w-full mt-2" />}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </AppShell>
    );
  }
  
  const storagePercentage = storageUsage ? getStoragePercentage() : 0;
  const basePlanName = storageUsage?.subscription_plan ? (
    i18n.language === 'en' ? 
    (storageUsage.subscription_plan.name_en || storageUsage.subscription_plan.name) : 
    storageUsage.subscription_plan.name
  ) : 'Free';
  
  // Add (Free) or (Kostenlos) for the free plan
  const isFree = storageUsage?.subscription_plan?.price_monthly_cents === 0;
  const freeLabel = i18n.language === 'en' ? ' (Free)' : ' (Kostenlos)';
  const planName = `${basePlanName}`;

  return (
    <AppShell 
      showHeader={false} 
      backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
    >
      <div className="container mx-auto p-4">
        {/* Email Confirmation Banner */}
        <EmailConfirmationBanner />
        
        {/* Profile Hero Section */}
        <DashboardProfileHero 
          firstName={firstName}
          t={t}
          className="mb-6"
        />
        
        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex">
            <Link to="/timeline" className="flex-1">
              <Card className="uplivion-card hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-uplivion-blue" />
                    {t('kpi.timeline')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-2xl font-bold">{dashboardData?.timelineEntriesCount || 0}</p>
                  <p className="text-xs text-gray-500">{t('kpi.memories')}</p>
                </CardContent>
              </Card>
            </Link>
          </div>
          
          <div className="flex">
            <Link to="/friends" className="flex-1">
              <Card className="uplivion-card hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Users className="h-4 w-4 mr-2 text-uplivion-blue" />
                    {t('kpi.friends')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-2xl font-bold">{dashboardData?.friendsCount || 0}</p>
                  <p className="text-xs text-gray-500">{t('kpi.connections')}</p>
                </CardContent>
              </Card>
            </Link>
          </div>
          
          <div className="flex">
            <Link to="/profile" className="flex-1">
              <Card className="uplivion-card hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <HardDrive className="h-4 w-4 mr-2 text-uplivion-blue" />
                    {t('kpi.storage')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {storageLoading ? (
                    <>
                      <Skeleton className="h-8 w-12 mb-1" />
                      <Skeleton className="h-3 w-16 mb-2" />
                      <Skeleton className="h-1 w-full" />
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold">{storagePercentage.toFixed(0)}%</p>
                      <p className="text-xs text-gray-500">{planName}</p>
                      <Progress 
                        value={storagePercentage} 
                        className="h-1 mt-2"
                        indicatorClassName={
                          isStorageCritical ? "bg-destructive" : 
                          isStorageWarning ? "bg-orange-500" : "bg-primary"
                        }
                      />
                    </>
                  )}
                </CardContent>
              </Card>
            </Link>
          </div>
          
          <div className="flex">
            <Link to="/friends" className="flex-1">
              <Card className="uplivion-card hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-uplivion-blue" />
                    {t('kpi.next')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {dashboardData?.nextBirthday ? (
                    <>
                      <p className="text-2xl font-bold">{dashboardData.nextBirthday.date}</p>
                      <p className="text-xs text-gray-500">{t('kpi.birthday', { name: dashboardData.nextBirthday.name })}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold">--</p>
                      <p className="text-xs text-gray-500">{t('kpi.noAppointments')}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
        
        {/* Create Section */}
        <CreateSection />
        
        {/* Challenge Section */}
        {FEATURE_CHALLENGES && !challengesLoading && challengeStatuses.length > 0 && (
          <div className="mb-6">
            {challengeStatuses.map((challengeStatus) => (
              <ChallengeCard
                key={challengeStatus.challenge.key}
                challengeStatus={challengeStatus}
                dashboardData={dashboardData}
              />
            ))}
          </div>
        )}        
        
        {/* Recent Activity */}
        <Card className="mb-6 uplivion-card">
          <CardHeader>
            <CardTitle>{t('activities.title')}</CardTitle>
            <CardDescription>{t('activities.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboardData?.recentActivities && dashboardData.recentActivities.length > 0 ? (
                dashboardData.recentActivities.map(activity => (
                  <div key={activity.id} className="flex items-start space-x-3 p-3 border-b border-gray-100 last:border-0">
                    <div className="w-10 h-10 rounded-full bg-uplivion-lightBlue flex items-center justify-center">
                      <span className="text-uplivion-blue font-semibold">{activity.user.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm">
                        <span className="font-semibold">{activity.user}</span> {activity.message}
                      </p>
                      <p className="text-xs text-gray-500">{activity.date}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('activities.empty.title')}</p>
                  <p className="text-sm">{t('activities.empty.subtitle')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        
        {/* Quick Links */}
        <div className="grid grid-cols-2 gap-4">
          <Link to="/timeline">
            <Card className="hover:shadow-md transition-shadow uplivion-card">
              <CardContent className="p-4 flex items-center justify-center h-24">
                <div className="text-center">
                  <Clock className="h-6 w-6 mx-auto mb-2 text-uplivion-blue" />
                  <p className="font-medium">{t('quickLinks.timeline')}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/friends">
            <Card className="hover:shadow-md transition-shadow uplivion-card">
              <CardContent className="p-4 flex items-center justify-center h-24">
                <div className="text-center">
                  <Users className="h-6 w-6 mx-auto mb-2 text-uplivion-blue" />
                  <p className="font-medium">{t('quickLinks.friends')}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </AppShell>
  );
};

export default Dashboard;
